import React, { Component } from "react";
import {
  Layout,
  Input,
  Button,
  Space,
  Typography,
  Empty,
  Tree,
  message,
  Dropdown,
  Card,
} from "antd";
import {
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  ShareAltOutlined,
  LockOutlined,
  FolderOutlined,
  GlobalOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import {
  getQueriesByType,
  deleteQuery,
  toggleQueryShared,
  canShareQueries,
} from "../../../utils/sqlQueryService";
import "./style.css";

const { DirectoryTree } = Tree;

const { Sider } = Layout;
const { Text } = Typography;
const { Search } = Input;

class SavedQueriesSidebar extends Component {
  state = {
    privateQueries: [],
    sharedQueries: [],
    searchTerm: "",
    loading: true,
  };

  componentDidMount() {
    this.loadQueries();
  }

  loadQueries = async () => {
    try {
      this.setState({ loading: true });
      // Single API call returns both private and shared queries
      const { private: privateQueries, shared } = await getQueriesByType();
      this.setState({
        privateQueries: privateQueries,
        sharedQueries: shared,
        loading: false,
      });
    } catch (error) {
      message.error("Failed to load saved queries");
      this.setState({ loading: false });
    }
  };

  handleSearch = (value) => {
    this.setState({ searchTerm: value.toLowerCase() });
  };

  handleLoadQuery = (query) => {
    if (this.props.onLoadQuery) {
      this.props.onLoadQuery(query);
    }
  };

  handleEditQuery = (query) => {
    if (this.props.onEditQuery) {
      this.props.onEditQuery(query);
    }
  };

  handleDeleteQuery = async (queryId) => {
    try {
      await deleteQuery(queryId);
      message.success("Query deleted successfully");
      this.loadQueries();
    } catch (error) {
      message.error(error.message || "Failed to delete query");
    }
  };

  handleToggleShare = async (query) => {
    try {
      await toggleQueryShared(query.id, !query.isShared);
      message.success(
        query.isShared
          ? "Query is now private"
          : "Query is now shared with all users"
      );
      this.loadQueries();
    } catch (error) {
      message.error(error.message || "Failed to update query");
    }
  };

  filterQueries = (queries) => {
    const { searchTerm } = this.state;
    if (!searchTerm) return queries;

    return queries.filter(
      (q) =>
        q.name.toLowerCase().includes(searchTerm) ||
        q.description?.toLowerCase().includes(searchTerm) ||
        q.query.toLowerCase().includes(searchTerm) ||
        q.folder?.toLowerCase().includes(searchTerm)
    );
  };

  buildTreeData = (queries, type) => {
    const folderMap = {};
    const rootQueries = [];

    // Group queries by folder
    queries.forEach(query => {
      if (!query.folder || query.folder.trim() === '') {
        rootQueries.push(query);
      } else {
        const folder = query.folder;
        if (!folderMap[folder]) {
          folderMap[folder] = [];
        }
        folderMap[folder].push(query);
      }
    });

    const treeData = [];

    // Build nested folder structure
    const folders = Object.keys(folderMap).sort();
    const processedPaths = new Set();

    folders.forEach(folderPath => {
      const parts = folderPath.split('/').filter(p => p.trim());
      let currentPath = '';
      
      parts.forEach((part, index) => {
        const parentPath = currentPath;
        currentPath = currentPath ? `${currentPath}/${part}` : part;
        
        if (processedPaths.has(currentPath)) {
          return;
        }
        processedPaths.add(currentPath);

        const isLeaf = currentPath === folderPath;
        const queriesAtThisLevel = isLeaf ? folderMap[folderPath] : [];

        const node = {
          title: part,
          key: `${type}-folder-${currentPath}`,
          isLeaf: false,
          children: queriesAtThisLevel.map(q => this.createQueryNode(q, type)),
          icon: <FolderOutlined />,
        };

        // Add to appropriate parent
        if (!parentPath) {
          treeData.push(node);
        } else {
          this.addToParent(treeData, parentPath.split('/'), node);
        }
      });
    });

    // Add root queries (uncategorized)
    if (rootQueries.length > 0) {
      treeData.push({
        title: 'Uncategorized',
        key: `${type}-uncategorized`,
        isLeaf: false,
        icon: <FolderOutlined style={{ color: "#8c8c8c" }} />,
        children: rootQueries.map(q => this.createQueryNode(q, type)),
      });
    }

    return treeData;
  };

  addToParent = (tree, pathParts, node) => {
    if (pathParts.length === 0) return;
    
    const currentPart = pathParts[0];
    const remainingParts = pathParts.slice(1);
    
    for (let item of tree) {
      if (item.title === currentPart) {
        if (remainingParts.length === 0) {
          item.children = item.children || [];
          item.children.push(node);
          return;
        } else {
          this.addToParent(item.children, remainingParts, node);
          return;
        }
      }
    }
  };

  createQueryNode = (query, type) => {
    const isAdmin = canShareQueries();
    
    // Build menu items for actions
    const menuItems = [
      {
        key: 'edit',
        label: 'Edit',
        icon: <EditOutlined />,
        onClick: () => this.handleEditQuery(query),
      },
    ];

    if (isAdmin) {
      menuItems.push({
        key: 'share',
        label: query.isShared ? 'Make Private' : 'Share',
        icon: query.isShared ? <LockOutlined /> : <ShareAltOutlined />,
        onClick: () => this.handleToggleShare(query),
      });
    }

    menuItems.push({
      key: 'delete',
      label: 'Delete',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => {
        // Show confirmation modal
        const confirmed = window.confirm('Delete this query? This action cannot be undone.');
        if (confirmed) {
          this.handleDeleteQuery(query.id);
        }
      },
    });

    return {
      title: (
        <Card
          size="small"
          hoverable
          className="query-item-card"
          styles={{
            body: { padding: '8px 10px' }
          }}
          onClick={(e) => {
            if (!e.target.closest('.query-actions')) {
              e.stopPropagation();
              this.handleLoadQuery(query);
            }
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="query-item-content">
              <div className="query-name" title={query.name}>
                {query.name}
              </div>
              {query.description && (
                <div className="query-description" title={query.description}>
                  {query.description}
                </div>
              )}
            </div>
            <div className="query-actions" onClick={(e) => e.stopPropagation()}>
              <Dropdown
                menu={{ items: menuItems }}
                trigger={['click']}
                placement="bottomRight"
              >
                <Button
                  type="text"
                  size="small"
                  icon={<MoreOutlined />}
                  onClick={(e) => e.stopPropagation()}
                />
              </Dropdown>
            </div>
          </div>
        </Card>
      ),
      key: `${type}-query-${query.id}`,
      isLeaf: true,
      icon: null,
      className: 'query-tree-node',
    };
  };


  render() {
    const { collapsed } = this.props;
    const { privateQueries, sharedQueries, loading } = this.state;

    const filteredPrivateQueries = this.filterQueries(privateQueries);
    const filteredSharedQueries = this.filterQueries(sharedQueries);

    if (collapsed) {
      return null;
    }

    // Build tree data for DirectoryTree
    const privateTreeData = this.buildTreeData(filteredPrivateQueries, 'private');
    const sharedTreeData = this.buildTreeData(filteredSharedQueries, 'shared');

    return (
      <Sider
        width={320}
        theme="light"
        style={{
          borderRight: "1px solid #f0f0f0",
          overflow: "auto",
          height: "100%",
          backgroundColor: "#fafafa",
        }}
      >
        <div style={{ padding: "12px" }}>
          <Search
            placeholder="Search queries..."
            allowClear
            size="small"
            prefix={<SearchOutlined />}
            onChange={(e) => this.handleSearch(e.target.value)}
            style={{ marginBottom: "12px" }}
          />

          {/* My Queries Section */}
          <div style={{ marginBottom: "16px" }}>
            <Space size={4} style={{ marginBottom: "8px" }}>
              <FolderOutlined style={{ color: "#1890ff", fontSize: "14px" }} />
              <Text strong style={{ fontSize: "12px" }}>Private Queries</Text>
              <Text type="secondary" style={{ fontSize: "11px" }}>({filteredPrivateQueries.length})</Text>
            </Space>
            {loading ? (
              <div style={{ padding: "20px", textAlign: "center" }}>Loading...</div>
            ) : privateTreeData.length > 0 ? (
              <DirectoryTree
                showIcon
                defaultExpandAll
                treeData={privateTreeData}
                style={{ fontSize: "11px" }}
                className="query-directory-tree"
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<span style={{ fontSize: "11px" }}>No saved queries</span>}
                style={{ padding: "12px 0" }}
              />
            )}
          </div>

          {/* Shared Queries Section */}
          <div>
            <Space size={4} style={{ marginBottom: "8px" }}>
              <GlobalOutlined style={{ color: "#52c41a", fontSize: "14px" }} />
              <Text strong style={{ fontSize: "12px" }}>Shared Queries</Text>
              <Text type="secondary" style={{ fontSize: "11px" }}>({filteredSharedQueries.length})</Text>
            </Space>
            {loading ? (
              <div style={{ padding: "20px", textAlign: "center" }}>Loading...</div>
            ) : sharedTreeData.length > 0 ? (
              <DirectoryTree
                showIcon
                defaultExpandAll
                treeData={sharedTreeData}
                style={{ fontSize: "11px" }}
                className="query-directory-tree"
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<span style={{ fontSize: "11px" }}>No shared queries</span>}
                style={{ padding: "12px 0" }}
              />
            )}
          </div>
        </div>
      </Sider>
    );
  }
}

export default SavedQueriesSidebar;

